/**
 * FilterBar with saved filter presets (hooks + modals).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { FilterBar } from './FilterBar';
import type { FilterBarProps } from './FilterBar';
import { useFilterBarWithPresetsCore } from './hooks/useFilterBarWithPresetsCore';
import { FilterBarPresetModals } from './presets/FilterBarPresetModals';

type Props = Omit<
  FilterBarProps,
  | 'presets'
  | 'activePresetId'
  | 'onLoadPreset'
  | 'onSaveClick'
  | 'onEditPreset'
  | 'onDeletePreset'
  | 'onSharePreset'
> & {
  tab: string;
  fromDate: string;
  toDate: string;
  onDateRangeChange?: (from: string, to: string) => void;
};

export const FilterBarWithPresets: FC<Props> = ({
  tab,
  fromDate,
  toDate,
  onDateRangeChange,
  filtersState,
  setFilters,
  ...rest
}) => {
  const core = useFilterBarWithPresetsCore({
    tab,
    fromDate,
    toDate,
    onDateRangeChange,
    filtersState,
    setFilters,
  });

  return (
    <>
      <FilterBar
        {...rest}
        filtersState={filtersState}
        setFilters={setFilters}
        presets={core.presets}
        activePresetId={core.activePresetId}
        onLoadPreset={core.handleLoadPreset}
        onSaveClick={() => {
          core.setEditingPreset(null);
          core.setSaveModalOpen(true);
        }}
        onEditPreset={core.handleEdit}
        onDeletePreset={core.handleDelete}
        onSharePreset={core.handleShareOpen}
      />
      <FilterBarPresetModals
        tab={tab}
        presets={core.presets}
        saveModalOpen={core.saveModalOpen}
        onSaveModalOpenChange={core.setSaveModalOpen}
        saveSubmitting={core.saveSubmitting}
        currentDateRangeDmy={core.currentDateRangeDmy}
        fromDateIso={fromDate}
        toDateIso={toDate}
        editingInitial={core.editingInitial}
        onSaveSubmit={core.handleSave}
        sharingPreset={core.sharingPreset}
        shareModalOpen={core.shareModalOpen}
        onShareModalOpenChange={core.setShareModalOpen}
        onShareUsers={core.onShareUsers}
        onUnshareUser={core.onUnshareUser}
        shareSubmitting={core.shareSubmitting}
      />
    </>
  );
};
